/* eslint-disable no-unused-expressions */
import { Stack, Typography, useMediaQuery, Box, Divider } from '@mui/material';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { LoginRequestDto } from 'tdc-web-backend/auth/schemas';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { EnumLinkedInFlow } from 'tdc-web-backend/enums/enums';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { LoginModal } from './components/LoginModal';
import useGetOne from '../../../utils/hooks/crud-hooks/useGetOne';
import ForgotPasswordModal from '../ForgotPassword/components/ForgotPasswordModal';
import TermsAndConditionsModal from './components/TermsAndConditionsModal';
import ResendSuccessModal from './components/ResendSuccessModal';
import NewPasswordSection from '../NewPassword/NewPasswordSection';
import theme from '../../../utils/theme';
import GoToBiggerScreenModal from '../../../components/go-to-bigger-screen-modal/GoToBiggerScreenModal';
import CustomButton from '../../../components/button/CustomButton';
import CustomInput from '../../../components/custom-input/CustomInput';
import useAuth from '../../../utils/hooks/useAuth';
import { EnumAuthLoginErrorType } from '../../../utils/context/Auth.context';
import Link from '../../../components/link/Link';
import LinkedInLoginButton from '../../../components/button/LinkedInLoginButton';

const LoginSection = () => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(search);
  const token = queryParams.get('token');
  const [isLoading, setIsLoading] = useState(false);

  const [showModal, setShowModal] = useState<boolean>(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState<boolean>(false);

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [openTermsModal, setOpenTermsModal] = useState(false);

  const isLessThanMedium = useMediaQuery(theme.breakpoints.down('md'));
  const [isGoToBiggerScreenModalOpen, setIsGoToBiggerScreenModalOpen] = useState(false);

  const { isLoadingLogin, login } = useAuth();

  const formMethods = useForm<LoginRequestDto>({
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onChange',
  });

  const {
    watch,
    handleSubmit,
    setError,
    formState: { errors },
  } = formMethods;

  const onSubmit = (data: LoginRequestDto) => {
    if (isLessThanMedium) {
      setIsGoToBiggerScreenModalOpen(true);
    } else {
      setIsLoading(true);
      login(data, {
        onSuccess: (response) => {
          const { user: userLoginData } = response.data;
          // if a user has a company
          if (userLoginData.membership?.company) {
            if (!userLoginData.agreementDate) {
              setOpenTermsModal(true);
            } else {
              navigate('/dashboard', { replace: true });
            }
          } else {
            navigate('/dashboard/create-company', { replace: true });
          }
        },
        onError: (error) => {
          setIsLoading(false);
          if (error.type === EnumAuthLoginErrorType.UserNotVerified) {
            setShowModal(true);
            return;
          }
          setError('password', {
            type: 'manual',
            message: 'Invalid password or email address. Please try again',
          });
        },
      });
    }
  };

  const { refetch, isLoading: isLoadingAuthUrl } = useGetOne<{ authorizationUrl: string }>({
    resource: `auth/linkedin/authorization-url?flow=${EnumLinkedInFlow.SignIn}`,
    enabled: false,
    onSuccess: (data) => {
      window.location.href = data.data.authorizationUrl;
    },
    onError: () => {
      setIsLoading(false);
    },
  });

  return (
    <>
      {isGoToBiggerScreenModalOpen && (
        <GoToBiggerScreenModal
          isGoToBiggerScreenModalOpen={isGoToBiggerScreenModalOpen}
          automaticallyShowModalIfBelowTreshold={false}
          onClick={() => navigate('/', { replace: true })}
        />
      )}

      <LoginModal
        open={showModal}
        setIsOpen={setShowModal}
        email={watch('email')}
        setOpenSuccessModal={setOpenSuccessModal}
      />

      <ResendSuccessModal
        email={watch('email')}
        open={openSuccessModal}
        setIsOpen={setOpenSuccessModal}
      />

      <ForgotPasswordModal
        open={showResetPassword}
        closeModal={() => setShowResetPassword(false)}
      />

      <TermsAndConditionsModal setIsOpen={setOpenTermsModal} isOpen={openTermsModal} />

      <>
        {token === null ? (
          <FormProvider {...formMethods}>
            {/* form */}
            <Box component="form" sx={{ pt: '2rem' }} onSubmit={handleSubmit(onSubmit)}>
              <Stack justifyContent="space-between" spacing={8}>
                {/* heading and subheading */}
                <Stack spacing={2}>
                  <Typography
                    sx={{
                      color: 'primaryDark.600',
                    }}
                    variant="headline1"
                  >
                    Welcome!
                  </Typography>

                  <Typography variant="body1" sx={{ color: 'primaryDark.500' }}>
                    Sign into your account to access your project management hub
                  </Typography>
                </Stack>

                {/* inputs & forgot password checkbox */}
                <Stack spacing={3} sx={{ pt: 2 }}>
                  {/* inputs */}
                  <Stack spacing={3}>
                    <CustomInput
                      input={<CustomInput.Text />}
                      name="email"
                      type="email"
                      placeholder="example@mail.com"
                      label="Email"
                      required
                      validationRegEx={{
                        value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                        message: 'Invalid email address',
                      }}
                    />

                    <CustomInput
                      input={<CustomInput.Text />}
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      label="Password"
                      required
                      placeholder="••••••••"
                      adornment={{
                        icon: showPassword ? (
                          <VisibilityIcon
                            sx={{ color: 'primaryDark.400', width: 22, height: 22 }}
                          />
                        ) : (
                          <VisibilityOffIcon
                            sx={{ color: 'primaryDark.400', width: 22, height: 22 }}
                          />
                        ),
                        onClick: () => {
                          setShowPassword((prevValue) => !prevValue);
                        },
                      }}
                    />
                  </Stack>

                  <Stack
                    spacing={2}
                    direction={{ xs: 'column', lg: 'row-reverse' }}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    {/* sign in button */}
                    <CustomButton
                      type="submit"
                      loading={isLoadingLogin || isLoadingAuthUrl || isLoading}
                      variant="primary"
                      sx={{ width: { xs: '100%', lg: '35%', xxl: '25%' } }}
                    >
                      Sign in
                    </CustomButton>

                    {!isLoadingLogin && !isLoadingAuthUrl && !isLoading && (
                      <Typography
                        variant="heading3"
                        color="primaryLight.500"
                        sx={{ cursor: 'pointer', textTransform: 'capitalize' }}
                        onClick={() => navigate('/reset-password')}
                      >
                        Forgot Password?
                      </Typography>
                    )}
                  </Stack>
                </Stack>

                <Stack spacing={2}>
                  <Divider>
                    {' '}
                    <Typography sx={{ pb: '4px' }}>or</Typography>{' '}
                  </Divider>

                  <Stack justifyContent="center" alignItems="center">
                    <LinkedInLoginButton
                      loading={isLoadingLogin || isLoadingAuthUrl || isLoading}
                      onClick={() => {
                        setIsLoading(true);
                        refetch();
                      }}
                    >
                      Sign in with LinkedIn
                    </LinkedInLoginButton>
                  </Stack>
                </Stack>

                {!isLoadingLogin && !isLoadingAuthUrl && !isLoading && (
                  <Typography
                    sx={{
                      alignSelf: 'center',
                      width: { xs: 'fit-content' },
                      fontSize: '14px',
                      pt: '2rem',
                    }}
                  >
                    Don’t have an account?{' '}
                    <Link to="/register" color="secondary.main" sx={{ fontWeight: 'bold' }}>
                      Sign Up
                    </Link>
                  </Typography>
                )}
              </Stack>
            </Box>
          </FormProvider>
        ) : (
          <NewPasswordSection token={token} />
        )}
      </>
    </>
  );
};

export default LoginSection;
